import type { MeetingDetail } from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Input, Select, Textarea } from '@fruuit/ui'
import { type FormEvent, useEffect, useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'
import { utcToZoned, zonedToUtc, zoneOptions } from '../calendar/time'

export interface RescheduleDialogProps {
  open: boolean
  onClose: () => void
  meeting: MeetingDetail
  onSubmit: (body: Record<string, unknown>) => Promise<unknown>
}

/**
 * Reschedule a published meeting (P-CAL-001): the original stays on record as
 * "Rescheduled" and a new published meeting links back to it, with the reason.
 */
export function RescheduleDialog({ open, onClose, meeting, onSubmit }: RescheduleDialogProps) {
  const [zone, setZone] = useState(meeting.timezone)
  const [starts, setStarts] = useState('')
  const [ends, setEnds] = useState('')
  const [cutoff, setCutoff] = useState('')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (open) {
      setZone(meeting.timezone)
      setStarts(utcToZoned(meeting.starts_at, meeting.timezone))
      setEnds(utcToZoned(meeting.ends_at, meeting.timezone))
      setCutoff('')
      setReason('')
      setErrors({})
      setFailure(null)
    }
  }, [open, meeting])

  async function submit(event: FormEvent) {
    event.preventDefault()
    const startsAt = zonedToUtc(starts, zone)
    const endsAt = zonedToUtc(ends, zone)
    const local: Record<string, string> = {}
    if (!startsAt) local.starts_at = 'Choose the new start.'
    if (!endsAt) local.ends_at = 'Choose the new end.'
    if (startsAt && endsAt && endsAt <= startsAt)
      local.ends_at = 'The meeting must end after it starts.'
    if (reason.trim().length < 8) local.reason = 'Record a reason of at least 8 characters.'
    if (Object.keys(local).length > 0) {
      setErrors(local)
      return
    }
    setErrors({})
    setFailure(null)
    setPending(true)
    try {
      const cutoffUtc = cutoff ? zonedToUtc(cutoff, zone) : null
      await onSubmit({
        revision: meeting.revision,
        reason: reason.trim(),
        starts_at: startsAt,
        ends_at: endsAt,
        timezone: zone,
        ...(cutoffUtc ? { participation_cutoff: cutoffUtc } : {}),
      })
      onClose()
    } catch (err) {
      if (isApiError(err)) setErrors(err.fieldErrors)
      setFailure(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Reschedule meeting"
      description="Investors see the new time with the reason it moved. Nothing is sent to the company or to calendars."
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="reschedule-form" loading={pending}>
            Publish new time
          </Button>
        </>
      }
    >
      <form id="reschedule-form" onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        {failure ? (
          <Callout tone="danger" role="alert" className="sm:col-span-2">
            {failure}
          </Callout>
        ) : null}
        <Input
          type="datetime-local"
          label="New start"
          required
          value={starts}
          error={errors.starts_at}
          onChange={(e) => setStarts(e.target.value)}
        />
        <Input
          type="datetime-local"
          label="New end"
          required
          value={ends}
          error={errors.ends_at}
          onChange={(e) => setEnds(e.target.value)}
        />
        <Select
          label="Time zone"
          options={zoneOptions(zone)}
          value={zone}
          onChange={(e) => setZone(e.target.value)}
        />
        <Input
          type="datetime-local"
          label="Participation deadline"
          hint="Optional. Defaults to the new start."
          value={cutoff}
          error={errors.participation_cutoff}
          onChange={(e) => setCutoff(e.target.value)}
        />
        <Textarea
          className="sm:col-span-2"
          label="Why is it moving?"
          required
          rows={3}
          maxLength={2000}
          value={reason}
          error={errors.reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </form>
    </Dialog>
  )
}
